import React, { useEffect, useMemo, useRef, useState } from 'react';
import './DailyQuiz.css';
import { loadConfig } from '../utils/config';
import { fetchKlines, Kline } from '../services/backend';
import AnalysisDashboard from './AnalysisDashboard';

type Interval = '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d';

type DailyQuizConfig = {
  id: string;
  symbol: string;
  interval: Interval;
  start: string; // ISO timestamp (UTC)
  end: string;   // ISO timestamp (UTC)
  // Correct answer for the quiz: 'up' | 'down' | 'sideways'
  answer?: 'up' | 'down' | 'sideways';
  // Optional: precomputed full analysis result to skip backend call
  // Shape: { similarity_analysis, returns_analysis, ai_analysis }
  precomputed_result?: any;
};

type Ohlc = {
  time: number; // unix seconds
  open: number;
  high: number;
  low: number;
  close: number;
};

type UserChoice = 'Bullish' | 'Bearish' | 'Sideways';

const QUIZ_API_BASE = (process.env.REACT_APP_QUIZ_API_URL || '').trim().replace(/\/$/, '');

function formatMeta(config?: DailyQuizConfig): string {
  if (!config) return '';
  const startD = new Date(config.start);
  const endD = new Date(config.end);
  const dateFmt = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC'
  });
  const timeFmt = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC'
  });
  const sameY = startD.getUTCFullYear() === endD.getUTCFullYear();
  const sameM = startD.getUTCMonth() === endD.getUTCMonth();
  const sameD = startD.getUTCDate() === endD.getUTCDate();
  const datePart = dateFmt.format(startD);
  const startTime = timeFmt.format(startD);
  const endTime = timeFmt.format(endD);
  const range = sameY && sameM && sameD
    ? `${datePart} ${startTime}–${endTime} UTC`
    : `${dateFmt.format(startD)} ${startTime} → ${dateFmt.format(endD)} ${endTime} UTC`;
  return `${config.symbol.toUpperCase()} • ${config.interval} • ${range}`;
}

function formatTodayLabel(): string {
  const today = new Date();
  const fmt = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC'
  });
  return fmt.format(today);
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function intervalToSeconds(interval: Interval): number {
  switch (interval) {
    case '1m': return 60;
    case '5m': return 5 * 60;
    case '15m': return 15 * 60;
    case '30m': return 30 * 60;
    case '1h': return 60 * 60;
    case '4h': return 4 * 60 * 60;
    case '1d': return 24 * 60 * 60;
    default: return 60 * 60;
  }
}

export const DailyQuiz: React.FC = () => {
  const [config, setConfig] = useState<DailyQuizConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<UserChoice | null>(null);
  const [dist, setDist] = useState<{ up: number; down: number; sideways: number } | null>(null);
  const [appConfig, setAppConfig] = useState<any>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [future30, setFuture30] = useState<Ohlc[] | null>(null);
  const [historyReal, setHistoryReal] = useState<Ohlc[] | null>(null);
  const [topHistory, setTopHistory] = useState<Ohlc[] | null>(null);
  const [allOhlc, setAllOhlc] = useState<Kline[] | null>(null);
  const [miniOpen, setMiniOpen] = useState(false);
  const [miniHist, setMiniHist] = useState<Ohlc[] | null>(null);
  const [miniFuture, setMiniFuture] = useState<Ohlc[] | null>(null);
  const [miniMeta, setMiniMeta] = useState<{ corr?: number; startMs?: number; endMs?: number } | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const hasPickedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const cfg = await loadConfig();
        if (mounted) setAppConfig(cfg);
        const res = await fetch('/daily-quiz.json', { cache: 'no-store' });
        if (!res.ok) throw new Error(`Failed to load daily-quiz.json (${res.status})`);
        const json = (await res.json()) as DailyQuizConfig;
        if (mounted) setConfig(json);
      } catch (e: any) {
        if (mounted) setError(e?.message || 'Failed to load config');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { /* noop */ };
  }, []);

  // Load distribution for current quiz id
  useEffect(() => {
    (async () => {
      if (!config?.id) return;
      try {
        const base = QUIZ_API_BASE;
        const res = await fetch(`${base}/api/quiz/${encodeURIComponent(config.id)}`, { cache: 'no-store' });
        if (!res.ok) return;
        const json = await res.json();
        if (json?.data) setDist(json.data);
      } catch {
        /* ignore */
      }
    })();
  }, [config?.id]);

  // Load real history segment for the top chart as soon as config is ready
  useEffect(() => {
    if (!config) return;
    let mounted = true;
    (async () => {
      try {
        const klines = await fetchKlines(appConfig, config.symbol, config.interval, 2000);
        const ohlc: Kline[] = (klines || []).map((k) => {
          const rawTime = Number(k.time);
          const timeSec = rawTime > 1e12 ? Math.floor(rawTime / 1000) : rawTime;
          return {
            ...k,
            time: timeSec,
            open: Number(k.open),
            high: Number(k.high),
            low: Number(k.low),
            close: Number(k.close),
            volume: k.volume != null ? Number(k.volume) : undefined
          };
        });
        const startSec = Math.floor(new Date(config.start).getTime() / 1000);
        const endSec = Math.floor(new Date(config.end).getTime() / 1000);
        setAllOhlc(ohlc);
        let startIndex = ohlc.findIndex((b) => Number(b.time) >= startSec);
        if (startIndex < 0) startIndex = 0;
        let endIndexInclusive = -1;
        for (let i = ohlc.length - 1; i >= 0; i--) {
          if (Number(ohlc[i].time) <= endSec) {
            endIndexInclusive = i;
            break;
          }
        }
        if (endIndexInclusive < 0) endIndexInclusive = Math.max(0, ohlc.length - 1);
        const endIndexExclusive = Math.min(ohlc.length, endIndexInclusive + 1);
        const topHist = ohlc.slice(startIndex, endIndexExclusive).map(b => ({
          time: Number(b.time),
          open: Number(b.open),
          high: Number(b.high),
          low: Number(b.low),
          close: Number(b.close)
        }));
        if (!mounted) return;
        setTopHistory(topHist.length ? topHist : null);
      } catch {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, [config, appConfig]);

  // Generic SVG candle chart renderer for a given series
  const renderChart = (inputSeries: Ohlc[] | null) => {
    if (!inputSeries || inputSeries.length === 0) return null;
    const padding = { top: 18, right: 18, bottom: 18, left: 42 };
    const width = 1000;
    const height = 260;
    const w = width - padding.left - padding.right;
    const h = height - padding.top - padding.bottom;
    const highs = inputSeries.map(b => b.high);
    const lows = inputSeries.map(b => b.low);
    const max = Math.max(...highs);
    const min = Math.min(...lows);
    const y = (v: number) => padding.top + (1 - (v - min) / (max - min || 1)) * h;
    const x = (i: number) => padding.left + (i / Math.max(1, inputSeries.length - 1)) * w;
    const candleWidth = clamp(w / Math.max(6, inputSeries.length * 1.5), 2, 8);

    const gridLines = 4;
    const ys = Array.from({ length: gridLines + 1 }, (_, i) => padding.top + (i / gridLines) * h);

    return (
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <g className="dq-grid">
          {ys.map((gy, idx) => (
            <line key={idx} x1={padding.left} x2={width - padding.right} y1={gy} y2={gy} />
          ))}
        </g>
        {inputSeries.map((b, i) => {
          const cx = x(i);
          const isUp = b.close >= b.open;
          const color = isUp ? '#10b981' : '#ef4444';
          const bodyTop = y(Math.max(b.open, b.close));
          const bodyBot = y(Math.min(b.open, b.close));
          return (
            <g key={i}>
              <line x1={cx} x2={cx} y1={y(b.high)} y2={y(b.low)} stroke={color} strokeWidth={1} />
              <rect
                x={cx - candleWidth / 2}
                y={bodyTop}
                width={candleWidth}
                height={Math.max(1.5, bodyBot - bodyTop)}
                fill={color}
                rx={1.5}
              />
            </g>
          );
        })}
      </svg>
    );
  };

  // Top chart: real history segment only
  const Chart = useMemo(() => renderChart(topHistory), [topHistory]);

  // Overlay chart with future30 highlighted
  const OverlayChart = useMemo(() => {
    if (!historyReal || !historyReal.length || !future30 || !future30.length) return null;
    const history = historyReal;
    const future = future30;
    const combined = [...history, ...future];
    const padding = { top: 18, right: 18, bottom: 18, left: 42 };
    const width = 1000;
    const height = 260;
    const w = width - padding.left - padding.right;
    const h = height - padding.top - padding.bottom;
    const highs = combined.map(b => b.high);
    const lows = combined.map(b => b.low);
    const max = Math.max(...highs);
    const min = Math.min(...lows);
    const y = (v: number) => padding.top + (1 - (v - min) / (max - min || 1)) * h;
    const x = (i: number) => padding.left + (i / Math.max(1, combined.length - 1)) * w;
    const candleWidth = clamp(w / Math.max(6, combined.length * 1.5), 2, 8);
    const gridLines = 4;
    const ys = Array.from({ length: gridLines + 1 }, (_, i) => padding.top + (i / gridLines) * h);
    const boundaryIndex = history.length - 1;
    const boundaryX = x(boundaryIndex) + candleWidth * 0.7;

    return (
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <filter id="futureGlow">
            <feGaussianBlur stdDeviation="2.2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="futureShade" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(59,130,246,0.08)"/>
            <stop offset="100%" stopColor="rgba(59,130,246,0.02)"/>
          </linearGradient>
        </defs>
        <g className="dq-grid">
          {ys.map((gy, idx) => (
            <line key={idx} x1={padding.left} x2={width - padding.right} y1={gy} y2={gy} />
          ))}
        </g>
        {/* Future region shading */}
        <rect
          x={Math.max(boundaryX, padding.left)}
          y={padding.top}
          width={Math.max(0, width - padding.right - boundaryX)}
          height={h}
          fill="url(#futureShade)"
        />
        {/* History candles */}
        {history.map((b, i) => {
          const cx = x(i);
          const isUp = b.close >= b.open;
          const color = isUp ? '#10b981' : '#ef4444';
          const bodyTop = y(Math.max(b.open, b.close));
          const bodyBot = y(Math.min(b.open, b.close));
          return (
            <g key={`h-${i}`}>
              <line x1={cx} x2={cx} y1={y(b.high)} y2={y(b.low)} stroke={color} strokeWidth={1} />
              <rect
                x={cx - candleWidth / 2}
                y={bodyTop}
                width={candleWidth}
                height={Math.max(1.5, bodyBot - bodyTop)}
                fill={color}
                rx={1.5}
              />
            </g>
          );
        })}
        {/* Boundary */}
        <line x1={boundaryX} x2={boundaryX} y1={padding.top} y2={padding.top + h} stroke="rgba(255,255,255,0.2)" strokeDasharray="4 4" />
        {/* Future candles with glow */}
        {future.map((b, fi) => {
          const i = history.length + fi;
          const cx = x(i);
          const isUp = b.close >= b.open;
          const color = isUp ? '#34d399' : '#f87171'; // brighter
          const bodyTop = y(Math.max(b.open, b.close));
          const bodyBot = y(Math.min(b.open, b.close));
          return (
            <g key={`f-${fi}`} filter="url(#futureGlow)">
              <line x1={cx} x2={cx} y1={y(b.high)} y2={y(b.low)} stroke={color} strokeWidth={1.3} />
              <rect
                x={cx - candleWidth / 2}
                y={bodyTop}
                width={candleWidth}
                height={Math.max(1.5, bodyBot - bodyTop)}
                fill={color}
                rx={1.5}
              />
            </g>
          );
        })}
      </svg>
    );
  }, [historyReal, future30]);

  const MiniOverlayChart = useMemo(() => {
    if (!miniHist || !miniFuture) return null;
    const history = miniHist;
    const future = miniFuture;
    const combined = [...history, ...future];
    const padding = { top: 16, right: 16, bottom: 16, left: 42 };
    const width = 640;
    const height = 220;
    const w = width - padding.left - padding.right;
    const h = height - padding.top - padding.bottom;
    const highs = combined.map(b => b.high);
    const lows = combined.map(b => b.low);
    const max = Math.max(...highs);
    const min = Math.min(...lows);
    const y = (v: number) => padding.top + (1 - (v - min) / (max - min || 1)) * h;
    const x = (i: number) => padding.left + (i / Math.max(1, combined.length - 1)) * w;
    const candleWidth = clamp(w / Math.max(6, combined.length * 1.5), 2, 8);
    const gridLines = 4;
    const ys = Array.from({ length: gridLines + 1 }, (_, i) => padding.top + (i / gridLines) * h);
    const boundaryIndex = history.length - 1;
    const boundaryX = x(boundaryIndex) + candleWidth * 0.7;
    return (
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <filter id="futureGlowMini">
            <feGaussianBlur stdDeviation="1.8" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="futureShadeMini" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(59,130,246,0.10)"/>
            <stop offset="100%" stopColor="rgba(59,130,246,0.03)"/>
          </linearGradient>
        </defs>
        <g className="dq-grid">
          {ys.map((gy, idx) => (
            <line key={idx} x1={padding.left} x2={width - padding.right} y1={gy} y2={gy} />
          ))}
        </g>
        <rect
          x={Math.max(boundaryX, padding.left)}
          y={padding.top}
          width={Math.max(0, width - padding.right - boundaryX)}
          height={h}
          fill="url(#futureShadeMini)"
        />
        {history.map((b, i) => {
          const cx = x(i);
          const isUp = b.close >= b.open;
          const color = isUp ? '#10b981' : '#ef4444';
          const bodyTop = y(Math.max(b.open, b.close));
          const bodyBot = y(Math.min(b.open, b.close));
          return (
            <g key={`mh-${i}`}>
              <line x1={cx} x2={cx} y1={y(b.high)} y2={y(b.low)} stroke={color} strokeWidth={1} />
              <rect x={cx - candleWidth / 2} y={bodyTop} width={candleWidth} height={Math.max(1.5, bodyBot - bodyTop)} fill={color} rx={1.5} />
            </g>
          );
        })}
        <line x1={boundaryX} x2={boundaryX} y1={padding.top} y2={padding.top + h} stroke="rgba(255,255,255,0.25)" strokeDasharray="4 4" />
        {future.map((b, fi) => {
          const i = history.length + fi;
          const cx = x(i);
          const isUp = b.close >= b.open;
          const color = isUp ? '#34d399' : '#f87171';
          const bodyTop = y(Math.max(b.open, b.close));
          const bodyBot = y(Math.min(b.open, b.close));
          return (
            <g key={`mf-${fi}`} filter="url(#futureGlowMini)">
              <line x1={cx} x2={cx} y1={y(b.high)} y2={y(b.low)} stroke={color} strokeWidth={1.2} />
              <rect x={cx - candleWidth / 2} y={bodyTop} width={candleWidth} height={Math.max(1.5, bodyBot - bodyTop)} fill={color} rx={1.5} />
            </g>
          );
        })}
      </svg>
    );
  }, [miniHist, miniFuture]);
  const formatRange = (sMs?: number | null, eMs?: number | null): string => {
    if (!sMs || !eMs) return '';
    try {
      const d1 = new Date(sMs);
      const d2 = new Date(eMs);
      const dateFmt = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        timeZone: 'UTC'
      });
      const timeFmt = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      });
      return `${dateFmt.format(d1)} ${timeFmt.format(d1)} → ${dateFmt.format(d2)} ${timeFmt.format(d2)} UTC`;
    } catch {
      return '';
    }
  };
  const handlePick = (value: UserChoice) => {
    if (hasPickedRef.current) return;
    hasPickedRef.current = true;
    setChoice(value);
    // Kick off real analysis
    if (!config) return;
    (async () => {
      try {
        // Submit vote
        const pick = value === 'Bullish' ? 'up' : value === 'Bearish' ? 'down' : 'sideways';
        try {
          const base = QUIZ_API_BASE;
          if (config?.id) {
            await fetch(`${base}/api/quiz/${encodeURIComponent(config.id)}/vote`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ choice: pick })
            });
            const after = await fetch(`${base}/api/quiz/${encodeURIComponent(config.id)}`, { cache: 'no-store' });
            if (after.ok) {
              const j = await after.json();
              if (j?.data) setDist(j.data);
            }
          }
        } catch {
          /* ignore vote errors to not block UX */
        }

        setAnalyzing(true);
        setAnalysisResult(null);
        setIsCorrect(null);
        setFuture30(null);
        setHistoryReal(null);
        // 1) Fetch klines
        const klines = await fetchKlines(appConfig, config.symbol, config.interval, 2000);
        // Normalize to numeric OHLC with seconds timestamp
        const ohlc: Kline[] = (klines || []).map((k) => {
          const rawTime = Number(k.time);
          const timeSec = rawTime > 1e12 ? Math.floor(rawTime / 1000) : rawTime; // normalize ms→s if needed
          return {
            ...k,
            time: timeSec,
            open: Number(k.open),
            high: Number(k.high),
            low: Number(k.low),
            close: Number(k.close),
            volume: k.volume != null ? Number(k.volume) : undefined
          };
        });
        // 2) Resolve indices for the configured time window
        const startSec = Math.floor(new Date(config.start).getTime() / 1000);
        const endSec = Math.floor(new Date(config.end).getTime() / 1000);
        let startIndex = ohlc.findIndex((b) => Number(b.time) >= startSec);
        if (startIndex < 0) startIndex = 0;
        let endIndexInclusive = -1;
        for (let i = ohlc.length - 1; i >= 0; i--) {
          if (Number(ohlc[i].time) <= endSec) {
            endIndexInclusive = i;
            break;
          }
        }
        if (endIndexInclusive < 0) endIndexInclusive = Math.max(0, ohlc.length - 1);
        const endIndexExclusive = Math.min(ohlc.length, endIndexInclusive + 1);
        // 2a) Store real history segment for overlay (configured window)
        const hist = ohlc.slice(startIndex, endIndexExclusive).map(b => ({
          time: Number(b.time),
          open: Number(b.open),
          high: Number(b.high),
          low: Number(b.low),
          close: Number(b.close)
        }));
        setHistoryReal(hist.length ? hist : null);
        
        // 2b) Compute and store next 30 bars (real data)
        const fwd = ohlc.slice(endIndexExclusive, Math.min(ohlc.length, endIndexExclusive + 30));
        const fwdSeries: Ohlc[] = fwd.map(b => ({
          time: Number(b.time),
          open: Number(b.open),
          high: Number(b.high),
          low: Number(b.low),
          close: Number(b.close)
        }));
        setFuture30(fwdSeries.length ? fwdSeries : null);

        // 2c) Optional correctness vs configured answer
        // 2c) Evaluate correctness vs configured answer (if provided)
        if (config.answer) {
          const pick = value === 'Bullish' ? 'up' : value === 'Bearish' ? 'down' : 'sideways';
          const correctNow = pick === config.answer;
          setIsCorrect(correctNow);
          if (correctNow) {
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 1500);
          } else {
            setShowConfetti(false);
          }
        }

        // 3) Analysis result: ONLY use precomputed result; no backend AI calls
        if (config.precomputed_result) {
          setAnalysisResult(config.precomputed_result);
        } else {
          setAnalysisResult(null);
        }
      } catch (e) {
        console.warn('Analysis failed:', e);
      } finally {
        setAnalyzing(false);
      }
    })();
  };

  const distPercents = useMemo(() => {
    const u = dist?.up ?? 0;
    const d = dist?.down ?? 0;
    const s = dist?.sideways ?? 0;
    const total = Math.max(1, u + d + s);
    const up = Math.round((u / total) * 100);
    const down = Math.round((d / total) * 100);
    const side = Math.max(0, 100 - up - down);
    return { up, down, side };
  }, [dist]);


  if (loading) {
    return (
      <div className="daily-quiz">
        <div className="dq-card">Loading Daily Quiz…</div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="daily-quiz">
        <div className="dq-card">Failed to load Daily Quiz configuration.</div>
      </div>
    );
  }

  return (
    <section className="daily-quiz">
      <div className="dq-card">
        <div className="dq-header">
          <div className="dq-left">
            <div className="dq-title">Daily Quiz</div>
            <span className="dq-today">{formatTodayLabel()}</span>
          </div>
          <div className="dq-meta">
            <span className="dq-range">{formatMeta(config)}</span>
          </div>
        </div>

        <div className="dq-top">
          <div className="dq-chart">
            {Chart}
          </div>
          <div className="dq-actions dq-actions-vertical" aria-label="Quiz options">
            <button
              className={`dq-btn up ${choice === 'Bullish' ? 'selected' : ''}`}
              onClick={() => handlePick('Bullish')}
              aria-pressed={choice === 'Bullish'}
              aria-disabled={choice !== null}
              disabled={choice !== null}
            >
              <span className="dq-label">I think it goes Up</span>
            </button>
            <button
              className={`dq-btn down ${choice === 'Bearish' ? 'selected' : ''}`}
              onClick={() => handlePick('Bearish')}
              aria-pressed={choice === 'Bearish'}
              aria-disabled={choice !== null}
              disabled={choice !== null}
            >
              <span className="dq-label">I think it goes Down</span>
            </button>
            <button
              className={`dq-btn side ${choice === 'Sideways' ? 'selected' : ''}`}
              onClick={() => handlePick('Sideways')}
              aria-pressed={choice === 'Sideways'}
              aria-disabled={choice !== null}
              disabled={choice !== null}
            >
              <span className="dq-label">I think it goes Sideways</span>
            </button>
          </div>
        </div>

        {choice && (
          <>
            <hr className="dq-divider" />
            <div className="dq-results">
              <div className="dq-panel">
                <h4>Your selection</h4>
                {isCorrect !== null && (
                  <div
                    className={`dq-result-banner ${isCorrect ? 'good' : 'bad'}`}
                    role="status"
                    aria-live="polite"
                  >
                    {isCorrect ? 'Correct!' : 'Incorrect'}
                  </div>
                )}
              </div>

              <div className="dq-panel">
                <h4 className="dq-panel-title">Outcome distribution</h4>
                <div className="dq-bars">
                  <div className="dq-bar">
                    <span>Bullish</span>
                    <div className="track">
                      <div className="fill" style={{ width: `${distPercents.up}%`, background: 'linear-gradient(90deg,#10b98188,#10b98133)' }} />
                    </div>
                    <span>{distPercents.up}%</span>
                  </div>
                  <div className="dq-bar">
                    <span>Bearish</span>
                    <div className="track">
                      <div className="fill" style={{ width: `${distPercents.down}%`, background: 'linear-gradient(90deg,#ef444488,#ef444433)' }} />
                    </div>
                    <span>{distPercents.down}%</span>
                  </div>
                  <div className="dq-bar">
                    <span>Sideways</span>
                    <div className="track">
                      <div className="fill" style={{ width: `${distPercents.side}%`, background: 'linear-gradient(90deg,#a78bfa88,#a78bfa33)' }} />
                    </div>
                    <span>{distPercents.side}%</span>
                  </div>
                </div>
                <div style={{ height: 10 }} />
                <div className="dq-legend">
                  <span className="dot-up" /> Up
                  <span className="dot-down" /> Down
                  <span className="dot-side" /> Sideways
                </div>
              </div>
            </div>
            {/* Moved up: Next 30 bars overlay chart */}
            <hr className="dq-divider" />
            <div className="dq-panel" style={{ background: 'transparent' }}>
              <h4>Future 30 bars</h4>
              <div className="dq-chart">
                {OverlayChart || <p className="dq-note">No future data available.</p>}
              </div>
              <p className="dq-note">Future bars are highlighted and overlaid on the original chart.</p>
            </div>
            <hr className="dq-divider" />
            <div className="dq-panel" style={{ background: 'transparent', border: 'none', padding: 0 }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#d1d5db' }}>
                Analysis Dashboard
                {analyzing ? ' • loading…' : ''}
              </h4>
              {analysisResult ? (
                <AnalysisDashboard
                  result={analysisResult}
                  onSelectSimilar={(seg) => {
                    (async () => {
                      if (!allOhlc || !config) return;
                      try {
                        setAnalyzing(true);
                        const sMs = Number(seg.start_time);
                        const eMs = Number(seg.end_time);
                        const sSec = Math.floor(sMs / 1000);
                        const eSec = Math.floor(eMs / 1000);
                        const needEndWithFuture = eSec + 30 * intervalToSeconds(config.interval);

                        let series = allOhlc;
                        const curMin = Number(series[0].time);
                        const curMax = Number(series[series.length - 1].time);
                        if (sSec < curMin || needEndWithFuture > curMax) {
                          // Fetch a larger window to cover this segment (+ future 30 bars)
                          const bigger = await fetchKlines(appConfig, config.symbol, config.interval, 10000);
                          const normalized = (bigger || []).map((k) => {
                            const raw = Number(k.time);
                            const t = raw > 1e12 ? Math.floor(raw / 1000) : raw;
                            return { ...k, time: t, open: Number(k.open), high: Number(k.high), low: Number(k.low), close: Number(k.close) };
                          });
                          if (normalized.length) {
                            series = normalized as unknown as Kline[];
                            setAllOhlc(series);
                          }
                        }

                        let sIdx = series.findIndex(b => Number(b.time) >= sSec);
                        if (sIdx < 0) sIdx = 0;
                        let eIdxInc = -1;
                        for (let i = series.length - 1; i >= 0; i--) {
                          if (Number(series[i].time) <= eSec) { eIdxInc = i; break; }
                        }
                        if (eIdxInc < 0) eIdxInc = Math.max(0, series.length - 1);
                        const eIdxEx = Math.min(series.length, eIdxInc + 1);

                        const hist = series.slice(sIdx, eIdxEx).map(b => ({
                          time: Number(b.time), open: Number(b.open), high: Number(b.high), low: Number(b.low), close: Number(b.close)
                        }));
                        const fwd = series.slice(eIdxEx, Math.min(series.length, eIdxEx + 30)).map(b => ({
                          time: Number(b.time), open: Number(b.open), high: Number(b.high), low: Number(b.low), close: Number(b.close)
                        }));

                        setMiniHist(hist.length ? hist : null);
                        setMiniFuture(fwd.length ? fwd : null);
                        setMiniMeta({ corr: Number(seg.correlation ?? 0), startMs: sMs, endMs: eMs });
                        setMiniOpen(true);
                      } catch (e) {
                        console.warn('Failed to build similar segment preview', e);
                      } finally {
                        setAnalyzing(false);
                      }
                    })();
                  }}
                />
              ) : (
                <p className="dq-note">
                  {analyzing
                    ? 'Running rectangle similarity analysis using backend…'
                    : 'No analysis yet or failed to fetch results.'}
                </p>
              )}
            </div>
            <div className="dq-footer-cta">
              <button
                className="dq-btn primary"
                onClick={() => window.open('https://app.alpha-pilot.net', '_blank')}
                aria-label="Learn more about Alphora AI"
              >
                Learn more about Alphora AI
              </button>
            </div>
        {/* Mini preview modal */}
        {miniOpen && (
          <div className="dq-modal-overlay" onClick={() => setMiniOpen(false)} role="dialog" aria-modal="true">
            <div className="dq-modal" onClick={(e) => e.stopPropagation()}>
              <div className="dq-modal-header">
                <div className="dq-title" style={{ fontSize: 16 }}>
                  Similar segment preview
                </div>
                <button className="dq-btn" onClick={() => setMiniOpen(false)} aria-label="Close">Close</button>
              </div>
              <div className="dq-badges" style={{ marginBottom: 10 }}>
                {miniMeta?.corr != null && <span className="dq-badge">ρ {miniMeta.corr.toFixed(3)}</span>}
                <span className="dq-badge">{formatRange(miniMeta?.startMs ?? null, miniMeta?.endMs ?? null)}</span>
              </div>
              <div className="dq-chart">
                {MiniOverlayChart}
              </div>
            </div>
          </div>
        )}
        {showConfetti && (
          <div className="dq-confetti" aria-hidden="true">
            {Array.from({ length: 24 }).map((_, i) => {
              const left = Math.random() * 100;
              const delay = Math.random() * 0.5;
              const dur = 0.9 + Math.random() * 0.8;
              const hue = Math.floor(180 + Math.random() * 180);
              return (
                <span
                  key={i}
                  className="piece"
                  style={{
                    left: `${left}%`,
                    animationDelay: `${delay}s`,
                    animationDuration: `${dur}s`,
                    background: `hsl(${hue} 85% 60%)`
                  }}
                />
              );
            })}
          </div>
        )}
          </>
        )}
      </div>
    </section>
  );
};

export default DailyQuiz;


